import { useEffect, useRef } from 'react';

/**
 * Height slide for submenus, ported from `animateHeight` in
 * `smartNavigation.js`.
 *
 * `.primary-nav ul` is `display: none` in the stylesheet, and the original
 * runtime flipped that inline while transitioning `height` from 0 to
 * `scrollHeight` and back. React cannot express that with a class alone — the
 * end state has to be `height: auto` so nested menus can grow — so the element
 * is still driven imperatively, just from an effect instead of a click handler.
 *
 * Timings are the ones `smartApp.js` passed in: 470ms open, 350ms close, with
 * `easeOutExpo` spelled out as its cubic-bezier.
 */

const OPEN_MS = 470;
const CLOSE_MS = 350;
const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function useSlideToggle(open: boolean) {
  const ref = useRef<HTMLUListElement | null>(null);
  const previousOpen = useRef<boolean | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const isFirstRun = previousOpen.current === null;
    const changed = previousOpen.current !== open;
    previousOpen.current = open;

    // On mount, land in the final state without animating: a menu that opens
    // itself because the current route is inside it should already be open when
    // the sidebar paints, not slide open a beat later.
    if (isFirstRun || !changed) {
      element.style.display = open ? 'flex' : 'none';
      return;
    }

    element.style.overflow = 'hidden';

    if (open) {
      element.style.display = 'flex';
      element.style.height = '0px';
      // Force layout so the browser has a 0 height to transition from.
      void element.offsetHeight;
      element.style.transition = `height ${OPEN_MS}ms ${EASING}`;
      element.style.height = `${element.scrollHeight}px`;
    } else {
      element.style.height = `${element.scrollHeight}px`;
      void element.offsetHeight;
      element.style.transition = `height ${CLOSE_MS}ms ${EASING}`;
      element.style.height = '0px';
    }

    const settle = (event?: TransitionEvent) => {
      // transitionend bubbles, so a nested submenu finishing its own slide would
      // otherwise end this element's animation early.
      if (event && event.target !== element) return;

      element.style.removeProperty('height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition');
      if (!open) element.style.display = 'none';

      element.removeEventListener('transitionend', settle);
      window.clearTimeout(fallback);
    };

    element.addEventListener('transitionend', settle);

    // A submenu hidden by an ancestor never fires transitionend, which would
    // leave height and overflow pinned. Clean up on the same schedule.
    const fallback = window.setTimeout(settle, (open ? OPEN_MS : CLOSE_MS) + 50);

    return () => {
      element.removeEventListener('transitionend', settle);
      window.clearTimeout(fallback);
    };
  }, [open]);

  return ref;
}
